import { useCallback, useState } from "react";
import { uploadApi } from "@shared/api/upload.api";
import { useUploadStore } from "@shared/store/upload.store";

interface UseFileUploadResult {
  uploading: boolean;
  progress: number;
  error: string | null;
  uploadFile: (file: File) => Promise<number | null>;
  reset: () => void;
}

const CHUNK_SIZE = 5 * 1024 * 1024;

export const useFileUpload = (): UseFileUploadResult => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { startUpload, updateProgress, completeUpload, setStatus, setUploadFinished } = useUploadStore();

  const uploadFile = useCallback(
    async (file: File): Promise<number | null> => {
      setUploading(true);
      setProgress(0);
      setError(null);

      let uploadId: string | null = null;

      try {
        const initResponse = await uploadApi.initUpload(file.name, file.size);
        uploadId = initResponse.uploadId;
        const { chunkSize, totalChunks } = initResponse;

        // Сохраняем в store для персистентности
        startUpload(uploadId, file.name, file.size, totalChunks);

        const effectiveChunkSize = chunkSize || CHUNK_SIZE;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * effectiveChunkSize;
          const end = Math.min(start + effectiveChunkSize, file.size);
          const chunk = file.slice(start, end);

          await uploadApi.uploadChunk(uploadId, i, chunk);

          const uploadedChunks = i + 1;
          const currentProgress = Math.round((uploadedChunks / totalChunks) * 100);

          setProgress(currentProgress);
          updateProgress(uploadId, uploadedChunks);
        }

        const completeResponse = await uploadApi.completeUpload(uploadId);

        if (completeResponse.success) {
          setProgress(100);
          completeUpload(uploadId);
          // Устанавливаем uploadFinished сразу после завершения загрузки
          setUploadFinished(uploadId, true);
          setUploading(false); // Сбрасываем состояние загрузки перед возвратом
          return completeResponse.fileId;
        } else {
          throw new Error("Failed to complete upload");
        }
      } catch (err) {
        let errorMessage = "Ошибка при загрузке файла";

        // Извлекаем сообщение от сервера
        if (err && typeof err === "object" && "response" in err) {
          const axiosError = err as { response?: { data?: { message?: string } } };
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setUploading(false);

        if (uploadId) {
          setStatus(uploadId, "error", errorMessage);
          // Удаляем из store через небольшую задержку, чтобы успело отобразиться сообщение об ошибке
          const finalUploadId = uploadId; // Сохраняем для closure
          setTimeout(() => {
            useUploadStore.getState().removeUpload(finalUploadId);
          }, 100);
        }

        return null;
      }
    },
    [startUpload, updateProgress, completeUpload, setStatus, setUploadFinished]
  );

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  return {
    uploading,
    progress,
    error,
    uploadFile,
    reset
  };
};
